import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API } from '../../api';
import { notifyError, notifySuccess } from '../../store/utils';
import queryString from 'query-string';
import { Button, Card, Input, Select, Space, Table, Typography } from 'antd';
import { PagedResultState } from '../../common/CommonResult';
import CustomerSelect from '../../lib/CustomerSelect';

const STATUS_OPTIONS = [
  { label: 'Гадаад захиалга бүртгэгдсэн', value: 'FOREIGN_ORDER' },
  { label: 'Гаалийн бүрдүүлэгч хүлээн авсан', value: 'ARRIVED_CUSTOMS' },
  { label: 'Гаальд мэдүүлсэн', value: 'CUSTOMS_CLEARANCE' },
  { label: 'Гаалийн бүрдүүлэлт дууссан', value: 'CUSTOMS_PENDING' },
  { label: 'Гаальд хураагдсан', value: 'CUSTOMS_FAILED' },
  { label: 'Гаалийн байгууллагаас хүлээн авсан', value: 'CUSTOMS_COMPLETE' },
];

const toArray = (value: any): string[] => {
  if (value === undefined || value === null || value === '') return [];
  return Array.isArray(value) ? value : [value];
};

export const ForeignOrderList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchState, changeSearchState] = useState<any>({});
  const [trackingNumber, setTrackingNumber] = useState('');
  const [packageNumber, setPackageNumber] = useState('');
  const [state, setState] = useState<PagedResultState<any>>({
    loading: true,
    result: {},
  });

  const fetchList = useCallback(async (query: any) => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const { result } = await API.get({ apiVersion: 'api' })(
        `/shipping/list?${queryString.stringify(query)}`
      );
      setState({
        loading: false,
        result,
      });
    } catch (error: any) {
      setState((prev) => ({ ...prev, loading: false }));
      notifyError(error.message);
    }
  }, []);

  useEffect(() => {
    if (location.search) {
      const query = queryString.parse(location.search);
      changeSearchState(query);
      setTrackingNumber((query.trackingNumber as string) || '');
      setPackageNumber((query.packageNumber as string) || '');
      fetchList(query);
    } else {
      navigate(`?page=1`);
    }
  }, [location.search, navigate, fetchList]);

  // шүүлт өөрчлөгдөхөд эхний хуудаснаас эхэлнэ, хоосон утгыг URL-д оруулахгүй
  const changeSearch = useCallback(
    (values: any) => {
      const query: any = { ...searchState, ...values, page: 1 };
      Object.keys(query).forEach((key) => {
        if (query[key] === undefined || query[key] === null || query[key] === '') {
          delete query[key];
        }
      });
      navigate(`?${queryString.stringify(query)}`);
    },
    [searchState, navigate]
  );

  const scanOrder = useCallback(
    async (trackingNumber: string, status: string) => {
      try {
        await API.post({ apiVersion: 'api' })(`/shipping/scan`, [{ trackingNumber, status }]);
        notifySuccess('Амжилттай');
        // төлөв өөрчлөгдсөн илгээмж шүүлтээс хасагдахаар жагсаалтаа дахин татна
        fetchList(queryString.parse(location.search));
      } catch (error: any) {
        notifyError(error.message);
      }
    },
    [location.search, fetchList]
  );

  const columns = useMemo(() => {
    return [
      {
        title: 'Баркод',
        dataIndex: 'trackingNumber',
        key: 'trackingNumber',
      },
      {
        title: 'Богц №',
        render: (text: any, record: any) => (
          <Typography.Link
            onClick={() => changeSearch({ packageNumber: record.orderPackage?.packageNumber })}
          >
            {record.orderPackage?.packageNumber}
          </Typography.Link>
        ),
        key: 'packageNumber',
      },
      {
        title: 'AirBill',
        render: (text: any, record: any) => (
          <a
            href={`/foreign/customs/${record.customs?.airBill}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {record.customs?.airBill}
          </a>
        ),
        key: 'airBill',
      },
      {
        title: 'Харилцагч',
        render: (text: any, record: any) => <span>{record.customer?.name}</span>,
        key: 'customer',
      },
      {
        title: 'Төлөв',
        render: (text: any, record: any) => <span>{record.status?.name}</span>,
        key: 'status',
      },
      {
        title: 'Action',
        render: (text: any, record: any) => (
          <span>
            {record.status?.cd === 'CUSTOMS_FAILED' && (
              <Button onClick={() => scanOrder(record.trackingNumber, 'CUSTOMS_PENDING')}>
                Гаалийн бүдүүлэлт дууссан
              </Button>
            )}
            {record.status?.cd === 'CUSTOMS_PENDING' && (
              <Button onClick={() => scanOrder(record.trackingNumber, 'CUSTOMS_COMPLETE')}>
                Гаалийн байгууллагаас хүлээн авсан
              </Button>
            )}
          </span>
        ),
        key: 'action',
      },
    ];
  }, [scanOrder, changeSearch]);

  return (
    <Card title={`Илээмж - ${state.result?.total || 0}`}>
      <Space wrap style={{ marginBottom: 15 }}>
        <Input.Search
          allowClear
          placeholder="Баркод / Захиалгын дугаар"
          value={trackingNumber}
          onChange={(e) => setTrackingNumber(e.target.value)}
          onSearch={(value) => changeSearch({ trackingNumber: value.trim() })}
          style={{ width: 250 }}
        />
        <Input.Search
          allowClear
          placeholder="Богцны дугаар"
          value={packageNumber}
          onChange={(e) => setPackageNumber(e.target.value)}
          onSearch={(value) => changeSearch({ packageNumber: value.trim() })}
          style={{ width: 200 }}
        />
        <Select
          mode="multiple"
          allowClear
          placeholder="Төлөв"
          style={{ minWidth: 250 }}
          maxTagCount="responsive"
          value={toArray(searchState.status)}
          options={STATUS_OPTIONS}
          onChange={(value) => changeSearch({ status: value })}
        />
        <CustomerSelect
          showSearch
          defaultQuery={{ type: 'CN' }}
          allowClear
          value={searchState.customer}
          onChange={(value) => changeSearch({ customer: value })}
        />
      </Space>
      <Table
        size="small"
        loading={state.loading}
        dataSource={state.result?.data || []}
        rowKey="no"
        onChange={(pagination) => {
          navigate(
            `?${queryString.stringify({
              ...searchState,
              page: pagination.current,
              size: pagination.pageSize,
            })}`
          );
        }}
        pagination={{
          current: state?.result?.page || 1,
          size: 'small',
          pageSize: state?.result?.size || 10,
          total: state?.result?.total,
        }}
        columns={columns}
      />
    </Card>
  );
};
